import type { CameraCalibrationMessage, PoseFrameMessage, WireLandmark } from '../protocol.ts';
import type { Mat3, Vec3 } from './planar.ts';

export const SHOULDER_WIDTH_PRIOR_M = 0.38;

export interface PersonPlacement {
  hipInTv: Vec3;
  billboardCenterInTv: Vec3;
  billboardWidthM: number;
  billboardHeightM: number;
  depthCameraZM: number;
  shoulderDepthM: number;
  floorCrossCheckDepthM?: number;
  usedFloorCrossCheck: boolean;
  posture: 'standing' | 'sitting';
}

type PlacementPoint = Pick<WireLandmark, 'x' | 'y' | 'z' | 'visibility'>;

const average = (a: PlacementPoint, b: PlacementPoint): PlacementPoint => ({
  x: (a.x + b.x) / 2,
  y: (a.y + b.y) / 2,
  z: (a.z + b.z) / 2,
  visibility: ((a.visibility ?? 1) + (b.visibility ?? 1)) / 2,
});

const transform = (matrix: Mat3, vector: Vec3): Vec3 => ({
  x: matrix[0] * vector.x + matrix[1] * vector.y + matrix[2] * vector.z,
  y: matrix[3] * vector.x + matrix[4] * vector.y + matrix[5] * vector.z,
  z: matrix[6] * vector.x + matrix[7] * vector.y + matrix[8] * vector.z,
});

const addScaled = (origin: Vec3, ray: Vec3, scale: number): Vec3 => ({
  x: origin.x + ray.x * scale,
  y: origin.y + ray.y * scale,
  z: origin.z + ray.z * scale,
});

function rayForPixel(xPx: number, yPx: number, calibration: CameraCalibrationMessage): Vec3 {
  const camera = calibration.camera;
  const cameraRay = {
    x: (xPx - camera.principalPointPx.x) / camera.focalLengthPx,
    y: (yPx - camera.principalPointPx.y) / camera.focalLengthPx,
    z: 1,
  };
  return transform(camera.rotationCameraToTv, cameraRay);
}

function visible(landmark: PlacementPoint | undefined): boolean {
  return Boolean(landmark) && (landmark!.visibility ?? 1) >= 0.45;
}

export function estimatePersonPlacement(
  frame: PoseFrameMessage,
  calibration: CameraCalibrationMessage,
  tvBottomEdgeM = 0.75,
): PersonPlacement | undefined {
  const landmarks = frame.landmarks;
  if (frame.status !== 'live' || !landmarks) return undefined;
  const byIndex = new Map(landmarks.map((landmark) => [landmark.index, landmark]));
  const leftShoulder = byIndex.get(11);
  const rightShoulder = byIndex.get(12);
  const leftHip = byIndex.get(23);
  const rightHip = byIndex.get(24);
  if (![leftShoulder, rightShoulder, leftHip, rightHip].every(visible)) return undefined;

  const shoulderPixels = Math.hypot(
    (leftShoulder!.x - rightShoulder!.x) * frame.frameWidthPx,
    (leftShoulder!.y - rightShoulder!.y) * frame.frameHeightPx,
  );
  if (shoulderPixels < 4) return undefined;
  const shoulderDepthM = Math.min(8, Math.max(0.4, calibration.camera.focalLengthPx * SHOULDER_WIDTH_PRIOR_M / shoulderPixels));
  const hip = average(leftHip!, rightHip!);
  const hipRay = rayForPixel(hip.x * frame.frameWidthPx, hip.y * frame.frameHeightPx, calibration);
  const camera = calibration.camera.cameraPositionInTv;
  const floorY = -calibration.tv.panelHeightM / 2 - tvBottomEdgeM;

  const leftAnkle = byIndex.get(27);
  const rightAnkle = byIndex.get(28);
  const anklesVisible = visible(leftAnkle) && visible(rightAnkle);
  const posture: 'standing' | 'sitting' = anklesVisible ? 'standing' : 'sitting';
  let floorCrossCheckDepthM: number | undefined;
  if (anklesVisible) {
    const foot = average(leftAnkle!, rightAnkle!);
    const footRay = rayForPixel(foot.x * frame.frameWidthPx, foot.y * frame.frameHeightPx, calibration);
    if (Math.abs(footRay.y) > 1e-5) floorCrossCheckDepthM = (floorY - camera.y) / footRay.y;
  } else if (Math.abs(hipRay.y) > 1e-5) {
    // A hip-height prior is only a cross-check: 0.58 m for a seated person.
    floorCrossCheckDepthM = (floorY + 0.58 - camera.y) / hipRay.y;
  }
  if (!Number.isFinite(floorCrossCheckDepthM) || floorCrossCheckDepthM! <= 0.2 || floorCrossCheckDepthM! > 10) {
    floorCrossCheckDepthM = undefined;
  }
  const agreesWithFloor = floorCrossCheckDepthM !== undefined
    && Math.abs(floorCrossCheckDepthM - shoulderDepthM) / shoulderDepthM <= 0.35;
  const depthCameraZM = agreesWithFloor
    ? shoulderDepthM * 0.75 + floorCrossCheckDepthM! * 0.25
    : shoulderDepthM;
  const centerRay = transform(calibration.camera.rotationCameraToTv, { x: 0, y: 0, z: 1 });

  return {
    hipInTv: addScaled(camera, hipRay, depthCameraZM),
    billboardCenterInTv: addScaled(camera, centerRay, depthCameraZM),
    billboardWidthM: (frame.frameWidthPx * depthCameraZM) / calibration.camera.focalLengthPx,
    billboardHeightM: (frame.frameHeightPx * depthCameraZM) / calibration.camera.focalLengthPx,
    depthCameraZM,
    shoulderDepthM,
    floorCrossCheckDepthM,
    usedFloorCrossCheck: agreesWithFloor,
    posture,
  };
}
